import { useState, type CSSProperties, type ReactNode } from 'react';
import { Box, GitFork, MousePointer2, Ruler, SquarePlus, StickyNote } from 'lucide-react';
import { activeSheet, useStore } from '../store';
import { MeasuredBaseModal } from './Palette';

/**
 * Altium-style vertical tool rail down the left side of the drawing area.
 * Context-aware: shows only the placement tools relevant to the active sheet.
 * "Select" clears the pending placement mode; the others either arm a
 * click-to-place mode (Note, Measure) or drop an item at the last insert
 * point. Standardized across every canvas sheet — block, schematic AND
 * mechanical — so adding a note or a base never requires the Library tab.
 */
export function LeftToolDock() {
  const kind = useStore((s) => activeSheet(s).kind);
  const pendingTool = useStore((s) => s.pendingTool);
  const setPendingTool = useStore((s) => s.setPendingTool);
  const addSplice = useStore((s) => s.addSplice);
  const addBlock = useStore((s) => s.addBlock);
  const addMechanicalItem = useStore((s) => s.addMechanicalItem);
  const lastPastePosition = useStore((s) => s.lastPastePosition);
  const [baseOpen, setBaseOpen] = useState(false);

  // Every canvas sheet gets the drawing-tool rail.
  if (kind !== 'block' && kind !== 'schematic' && kind !== 'mechanical') return null;

  const insertPos = () => lastPastePosition ?? { x: 120, y: 120 };

  return (
    <>
      <div style={railStyle}>
        <ToolButton
          label="Select"
          hint="Select / move (Esc)"
          active={!pendingTool}
          onClick={() => setPendingTool(null)}
        >
          <MousePointer2 size={17} />
        </ToolButton>
        <ToolButton
          label="Note"
          hint="Place a note — click on the sheet"
          active={pendingTool === 'annotation'}
          onClick={() => setPendingTool('annotation')}
        >
          <StickyNote size={17} />
        </ToolButton>

        <div style={sepStyle} />

        {kind === 'schematic' && (
          <ToolButton label="Splice" hint="Add a splice / junction" onClick={() => addSplice(insertPos())}>
            <GitFork size={17} />
          </ToolButton>
        )}
        {kind === 'block' && (
          <ToolButton label="Block" hint="Add a block" onClick={() => addBlock(insertPos())}>
            <SquarePlus size={17} />
          </ToolButton>
        )}
        {kind === 'mechanical' && (
          <>
            <ToolButton
              label="Base"
              hint="Add a measured base / chassis"
              onClick={() => setBaseOpen(true)}
            >
              <Box size={17} />
            </ToolButton>
            <ToolButton
              label="Measure"
              hint="Measure — click two points on the sheet"
              active={pendingTool === 'measure'}
              onClick={() => setPendingTool('measure')}
            >
              <Ruler size={17} />
            </ToolButton>
          </>
        )}
      </div>

      {baseOpen && (
        <MeasuredBaseModal
          onCancel={() => setBaseOpen(false)}
          onSubmit={(base) => {
            addMechanicalItem(insertPos(), base);
            setBaseOpen(false);
          }}
        />
      )}
    </>
  );
}

function ToolButton({
  label,
  hint,
  active,
  onClick,
  children,
}: {
  label: string;
  hint: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={hint}
      aria-label={label}
      data-active={active ? 'true' : undefined}
      className="cad-btn"
      style={{
        width: 32,
        height: 32,
        padding: 0,
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

const railStyle: CSSProperties = {
  width: 40,
  flexShrink: 0,
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderRight: '1px solid var(--chrome-border)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  paddingTop: 8,
};

const sepStyle: CSSProperties = {
  width: 22,
  height: 1,
  background: 'var(--panel-border-strong)',
  margin: '2px 0',
};
